import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlarmClock,
  ArrowUpRight,
  CalendarCheck,
  CalendarX,
  Clock,
  IndianRupee,
  Plane,
  TrendingUp,
  UserCheck,
  UserRound,
  UserX,
  Users,
  Wallet
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  getDailyAttendanceReport,
  getDailyPayoutReport,
  getMonthlyPayReport,
  getMonthlyStaffAttendanceReport
} from '../../services/api/implementation/impl';
import { Tile } from '../../../shared/components/ui/primitives';

/**
 * The Workforce dashboard — what the hub's "Overview" slot used to link to
 * (itself). Built only from the report endpoints the Reports screen already
 * calls, so it needs no backend change and shows nobody anything their own
 * screens would not.
 *
 * Laid out like the main dashboard: one white panel per area with a title,
 * a one-line subtitle and an "Open …" button, figures on top, chart under.
 * The buttons are the brand green Operations and Inventory use for "more
 * details" — the main dashboard's blue stood out too much here.
 * The figures are HR/Labour's Company-style cards (Tile variant="card").
 *
 * Each panel is shown only when the person can already open the screen it
 * summarises, and hides itself if the API refuses — a dashboard that shows an
 * error for a report you were never given is noise, not information.
 */

const CHART = {
  present: '#2A565E', // brand green, same as the Open buttons
  half: '#0369A1',
  absent: '#BE123C',
  notMarked: '#B45309',
  otOnly: '#7E22CE',
  bar: '#2A565E',
  area: '#4F46E5'
};

const WorkforceDashboard = ({ paths }) => {
  const showLabour = paths.has('/builder/workforce/reports');
  const showStaff = paths.has('/builder/hr/attendance');

  if (!showLabour && !showStaff) return null;

  return (
    <div className="space-y-5">
      {showLabour && <LabourToday />}
      {showLabour && <LabourMoney />}
      {showStaff && <StaffMonth />}
    </div>
  );
};

// --- labour: today's muster --------------------------------------------------

const LabourToday = () => {
  const navigate = useNavigate();
  const date = todayKey();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['workforceDashboard', 'labourToday', date],
    queryFn: () => getDailyAttendanceReport({ date }),
    retry: false
  });
  if (isError) return null;

  const meta = data?.meta ?? {};
  const roster = meta.roster ?? 0;
  const slices = [
    { name: 'Present', value: meta.present ?? 0, color: CHART.present },
    { name: 'Half day', value: meta.half_day ?? 0, color: CHART.half },
    { name: 'OT only', value: meta.ot_only ?? 0, color: CHART.otOnly },
    { name: 'Absent', value: meta.absent ?? 0, color: CHART.absent },
    { name: 'Not marked', value: meta.not_marked ?? 0, color: CHART.notMarked }
  ].filter((s) => s.value > 0);

  return (
    <Panel
      title="Labour today"
      subtitle={`Muster for ${prettyDate(date)} across all sites`}
      action="Open muster"
      onAction={() => navigate('/builder/operations/labour/muster')}
      loading={isLoading}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="grid grid-cols-2 gap-3 lg:col-span-2">
          <Tile variant="card" label="Roster" value={roster} icon={<Users size={18} />} />
          <Tile
            variant="card"
            label="Present"
            value={(meta.present ?? 0) + (meta.half_day ?? 0)}
            hint={meta.half_day ? `${meta.half_day} half day` : 'Full and half day'}
            icon={<UserCheck size={18} />}
            tone="success"
          />
          <Tile
            variant="card"
            label="Absent"
            value={meta.absent ?? 0}
            icon={<UserX size={18} />}
            tone="danger"
          />
          <Tile
            variant="card"
            label="Not marked"
            value={meta.not_marked ?? 0}
            hint="Still to be recorded"
            icon={<AlarmClock size={18} />}
            tone="warning"
            onClick={() => navigate('/builder/operations/labour/muster')}
          />
        </div>

        <ChartBox title="Muster status" footer={<Legend items={slices} />}>
          {roster === 0 ? (
            <ChartEmpty text="No active workmen on the roster." />
          ) : (
            <div className="relative h-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="58%"
                    outerRadius="85%"
                    paddingAngle={slices.length > 1 ? 2 : 0}
                    stroke="none">
                    {slices.map((s) => (
                      <Cell key={s.name} fill={s.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} of ${roster}`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold tabular-nums text-[#111827]">
                  {percent((meta.present ?? 0) + (meta.half_day ?? 0), roster)}
                </span>
                <span className="text-xs text-gray-500">turned up</span>
              </div>
            </div>
          )}
        </ChartBox>
      </div>
    </Panel>
  );
};

// --- labour: money -------------------------------------------------------------

const LabourMoney = () => {
  const navigate = useNavigate();
  const month = monthKey();
  const range = useMemo(() => ({ from: daysAgoKey(13), to: todayKey() }), []);

  const pay = useQuery({
    queryKey: ['workforceDashboard', 'labourPay', month],
    queryFn: () => getMonthlyPayReport({ month }),
    retry: false
  });
  const payouts = useQuery({
    queryKey: ['workforceDashboard', 'labourPayouts', range],
    queryFn: () => getDailyPayoutReport(range),
    retry: false
  });
  if (pay.isError && payouts.isError) return null;

  const meta = pay.data?.meta ?? {};
  const byDay = new Map((payouts.data?.data?.days ?? []).map((d) => [d.date, d.amount]));
  const series = lastNDays(14).map((key) => ({
    key,
    label: prettyDate(key),
    amount: byDay.get(key) ?? 0
  }));
  const fortnight = series.reduce((s, d) => s + d.amount, 0);

  return (
    <Panel
      title="Labour wages"
      subtitle={`${monthName(month)} · earned, paid and still owed`}
      action="Open wages"
      onAction={() => navigate('/builder/operations/labour/wages')}
      loading={pay.isLoading || payouts.isLoading}>
      {!pay.isError && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile
            variant="card"
            label="Earned this month"
            value={money(meta.total_earned)}
            icon={<TrendingUp size={18} />}
          />
          <Tile
            variant="card"
            label="Paid this month"
            value={money(meta.total_paid)}
            icon={<IndianRupee size={18} />}
            tone="success"
          />
          <Tile
            variant="card"
            label="Outstanding"
            value={money(meta.total_outstanding)}
            hint="All time, still owed"
            icon={<Wallet size={18} />}
            tone="warning"
          />
        </div>
      )}

      {!payouts.isError && (
        <ChartBox
          title="Paid out, last 14 days"
          aside={money(fortnight)}
          className="mt-4"
          height="h-56">
          {fortnight === 0 ? (
            <ChartEmpty text="No wage payments recorded in the last 14 days." />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#EEF2F6" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                  tickFormatter={compactMoney}
                />
                <Tooltip
                  cursor={{ fill: '#F1F5F9' }}
                  formatter={(v) => [money(v), 'Paid']}
                  labelFormatter={(_, p) => prettyDate(p?.[0]?.payload?.key)}
                />
                <Bar dataKey="amount" fill={CHART.bar} radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartBox>
      )}
    </Panel>
  );
};

// --- employees -----------------------------------------------------------------

const StaffMonth = () => {
  const navigate = useNavigate();
  const month = monthKey();
  const today = todayKey();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['workforceDashboard', 'staffMonth', month],
    queryFn: () => getMonthlyStaffAttendanceReport({ month }),
    retry: false
  });
  if (isError) return null;

  const rows = data?.data?.rows ?? [];
  const days = (data?.data?.days ?? []).filter((d) => d <= today);
  const todayCells = rows.map((r) => r.cells?.[today] ?? '');
  const count = (codes) => todayCells.filter((c) => codes.includes(c)).length;

  const present = count(['P', 'H', 'R']);
  const onLeave = count(['L']);
  const absent = count(['A']);
  const offToday = count(['W', 'F']);
  const notMarked = rows.length - present - onLeave - absent - offToday;

  const trend = days.map((d) => ({
    key: d,
    label: String(Number(d.slice(-2))),
    present: rows.reduce((n, r) => {
      const c = r.cells?.[d];
      if (c === 'P' || c === 'R') return n + 1;
      if (c === 'H') return n + 0.5;
      return n;
    }, 0)
  }));

  return (
    <Panel
      title="Employees"
      subtitle={`Office and site staff · today and ${monthName(month)} so far`}
      action="Open attendance"
      onAction={() => navigate('/builder/hr/attendance')}
      loading={isLoading}>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tile
          variant="card"
          label="Employees"
          value={rows.length}
          icon={<UserRound size={18} />}
          onClick={() => navigate('/builder/hr/employees')}
        />
        <Tile
          variant="card"
          label="Present today"
          value={present}
          icon={<CalendarCheck size={18} />}
          tone="success"
        />
        <Tile
          variant="card"
          label="On leave"
          value={onLeave}
          icon={<Plane size={18} />}
          tone="info"
          onClick={() => navigate('/builder/hr/leave')}
        />
        <Tile
          variant="card"
          label="Absent"
          value={absent}
          icon={<CalendarX size={18} />}
          tone="danger"
        />
        <Tile
          variant="card"
          label="Not marked"
          value={Math.max(notMarked, 0)}
          hint={offToday ? `${offToday} off today` : 'No punch yet'}
          icon={<Clock size={18} />}
          tone="warning"
        />
      </div>

      <ChartBox
        title={`Present per day, ${monthName(month)}`}
        aside={`${rows.length} on the books`}
        className="mt-4"
        height="h-56">
        {rows.length === 0 ? (
          <ChartEmpty text="No employees yet." />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="wfStaffArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART.area} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={CHART.area} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#EEF2F6" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#6B7280' }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                allowDecimals={false}
                domain={[0, Math.max(rows.length, 1)]}
                tick={{ fontSize: 11, fill: '#6B7280' }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip
                formatter={(v) => [v, 'Present']}
                labelFormatter={(_, p) => prettyDate(p?.[0]?.payload?.key)}
              />
              <Area
                type="monotone"
                dataKey="present"
                stroke={CHART.area}
                strokeWidth={2}
                fill="url(#wfStaffArea)"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartBox>
    </Panel>
  );
};

// --- layout pieces (match the main dashboard's panels) --------------------------

const Panel = ({ title, subtitle, action, onAction, loading, children }) => (
  <section className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="text-xl font-semibold text-[#1E4A53]">{title}</h3>
        <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
      </div>
      {action && (
        <button
          type="button"
          onClick={onAction}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2">
          {action}
          <ArrowUpRight size={16} />
        </button>
      )}
    </div>
    {loading ? <PanelSkeleton /> : children}
  </section>
);

/* `footer` sits under the fixed-height chart area, not inside it: the donut's
 * legend used to live inside and spilled out below the card. */
const ChartBox = ({ title, aside, className = '', height = 'h-44', footer, children }) => (
  <div className={`flex flex-col rounded-xl border border-[#E5EAF1] p-3 ${className}`}>
    <div className="mb-2 flex items-baseline justify-between gap-2">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</p>
      {aside && <p className="text-sm font-semibold tabular-nums text-[#111827]">{aside}</p>}
    </div>
    <div className={height}>{children}</div>
    {footer}
  </div>
);

const ChartEmpty = ({ text }) => (
  <div className="flex h-full items-center justify-center text-center text-sm text-gray-500">
    {text}
  </div>
);

const Legend = ({ items }) =>
  items.length ? (
    <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1">
      {items.map((s) => (
        <span key={s.name} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
          {s.name} <span className="tabular-nums text-gray-900">{s.value}</span>
        </span>
      ))}
    </div>
  ) : null;

const PanelSkeleton = () => (
  <div className="animate-pulse space-y-3 motion-reduce:animate-none" aria-hidden="true">
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-[76px] rounded-xl bg-gray-100" />
      ))}
    </div>
    <div className="h-44 rounded-xl bg-gray-100" />
  </div>
);

// --- helpers --------------------------------------------------------------------

function pad(n) {
  return String(n).padStart(2, '0');
}

function keyOf(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayKey() {
  return keyOf(new Date());
}

function daysAgoKey(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return keyOf(d);
}

function lastNDays(n) {
  return Array.from({ length: n }, (_, i) => daysAgoKey(n - 1 - i));
}

function monthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function monthName(month) {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long' });
}

function prettyDate(key) {
  if (!key) return '';
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short'
  });
}

function money(value) {
  const n = Number(value || 0);
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function compactMoney(value) {
  const n = Number(value || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (n >= 1e3) return `₹${Math.round(n / 1e3)}k`;
  return `₹${n}`;
}

function percent(part, whole) {
  if (!whole) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}

export default WorkforceDashboard;
